import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";

import { cn } from "@/lib/utils";

const Tabs = TabsPrimitive.Root;

interface TabsListProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> {
  paddingX?: string;
}

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  TabsListProps
>(({ className, paddingX, ...props }, ref) => (
  <div className="relative">
    <TabsPrimitive.List
      ref={ref}
      className={cn(
        "inline-flex bg-transparent p-0 gap-6",
        paddingX,
        className
      )}
      {...props}
    />
    <div className="absolute bottom-0 left-0 right-0 border-b border-gray-200"></div>
  </div>
));
TabsList.displayName = TabsPrimitive.List.displayName;

interface TabsTriggerProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> {
  activeColor?: string;
}

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({ className, activeColor, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    style={
      activeColor
        ? ({
            "--active-color": activeColor,
          } as React.CSSProperties)
        : undefined
    }
    className={cn(
      "inline-flex items-center py-3 text-sm font-medium text-gray-600 ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:text-silvaconsult-primary-500 relative group",
      activeColor && "data-[state=active]:text-[var(--active-color)]",
      className
    )}
    {...props}
  >
    {props.children}
    <div
      className={cn(
        "absolute bottom-0 left-0 right-0 h-[3px] opacity-0 group-data-[state=active]:opacity-100 group-data-[state=active]:rounded-t-sm transition-all",
        activeColor
          ? "group-data-[state=active]:bg-[var(--active-color)]"
          : "group-data-[state=active]:bg-silvaconsult-primary-500"
      )}
    />
  </TabsPrimitive.Trigger>
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
